import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { CustomAlert, CustomButton } from '../../shared/uiComponents';
import { errorNormalizer } from '../../shared/Helpers/functions';
import { MultipleSearchSelect, SelectDataProperties } from '../../shared/uiComponents/Dropdown/searchSelect';
import { ButtonWrapper } from '../../components/StyledComponents';

import { DispatchProperties, useSelector } from '../../redux/store';
import { assignBcbas, getBCBAs, getClinicalSupervisorsBCBAs } from '../../redux/State/userSlice';

const Content = ({ onClose }: { onClose: () => void }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { userId } = useParams();

	const [bcbaIds, setBcbaIds] = useState<string[]>([]);
	const [pageSize, setPageSize] = useState<number>(8);
	const [searchString, setSearchString] = useState<string>('');

	const bcbas = useSelector((state) => state.user.bcbas);
	const selectedBcbas = useSelector((state) => state.clinicalSupervisor.clinicalSupervisorsBcbas);

	const [data, setData] = useState<SelectDataProperties[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const bcbasHandler = (values: string[]) => {
		setBcbaIds(values);
	};

	const saveSupervisorsHandler = () => {
		if (!userId || !bcbaIds) return;
		dispatch(
			assignBcbas({
				clinicalSupervisorId: userId,
				bcbaIds,
			}),
		)
			.then(unwrapResult)
			.then(() => toast('Success'))
			.catch(errorNormalizer)
			.finally(onClose);
	};

	const searchHandler = (value: string) => setSearchString(value);

	useEffect(() => {
		if (!bcbas.query || !selectedBcbas) return;
		const contentData = bcbas.query
			.filter((x) => !bcbaIds.includes(x.id))
			.map((z) => ({ id: z.id, label: z.fullName }));

		const selectedData = selectedBcbas
			.filter((x) => bcbaIds.includes(x.id))
			.map((z) => ({ id: z.id, label: z.fullName }));

		if (searchString) {
			setData(contentData);

			return;
		}
		setData([...selectedData, ...contentData]);
	}, [bcbas.query, bcbaIds, selectedBcbas, searchString]);

	useEffect(() => {
		if (!selectedBcbas) return;
		setIsLoading(true);
		const timeout = setTimeout(() => {
			dispatch(
				getBCBAs({
					page: '1',
					pageSize: `${pageSize}`,
					searchString,
				}),
			)
				.then(unwrapResult)
				.finally(() => setIsLoading(false));
		}, 1000);

		// TODO - return void as in return () => void clearTimeout(timeout);
		// eslint-disable-next-line
		return () => clearTimeout(timeout);
	}, [pageSize, searchString, selectedBcbas, dispatch]);

	useEffect(() => {
		if (!userId) return;
		dispatch(getClinicalSupervisorsBCBAs(userId))
			.then(unwrapResult)
			.then((response) => {
				const ids = response.map((x) => x.id);
				setBcbaIds(ids);
			})
			.catch(errorNormalizer);
	}, [dispatch, userId]);

	return (
		<div style={{ textAlign: 'center' }}>
			<MultipleSearchSelect
				data={data}
				setValues={bcbasHandler}
				values={bcbaIds ? bcbaIds : []}
				label="BCBAs"
				className="marginBottom16"
				setPageSize={setPageSize}
				searchString={searchString}
				setSearchString={searchHandler}
				loading={false}
				hasMore={bcbas.hasNextPage}
				width="350px"
			/>
			<ButtonWrapper>
				<CustomButton title="Cancel" onClick={onClose} buttonType="secondaryGray" />
				<CustomButton title="Save" onClick={saveSupervisorsHandler} loading={isLoading} />
			</ButtonWrapper>
		</div>
	);
};

const AssignBCBA = () => {
	const [open, setOpen] = useState<boolean>(false);
	const user = useSelector((state) => state.user.user);
	const isAssigning = useSelector((state) => state.user.loading);

	return (
		<>
			<CustomButton
				title="Edit BCBAs"
				onClick={() => setOpen(true)}
				className="marginBottom16"
				buttonType="outlined"
				disabled={isAssigning}
			/>
			<CustomAlert
				isBusy={isAssigning}
				open={open}
				onClose={() => setOpen(false)}
				Content={({ onClose }) => <Content onClose={onClose} />}
				title={`Assign BCBAs to ${user.fullName}`}
				maxWidth="xs"
			/>
		</>
	);
};

export default AssignBCBA;
